import { useState } from "react";
import { format, parseISO, startOfToday } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useBlockedDates } from "../lib/usePortfolio";

const DARK_CALENDAR = {
  caption_label: "text-sm font-serif text-ivory",
  nav_button: "h-7 w-7 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne inline-flex items-center justify-center bg-transparent",
  head_cell: "text-stone-500 w-9 font-mono text-[10px] uppercase tracking-widest",
  cell: "relative p-0 text-center text-sm",
  day: "h-9 w-9 p-0 font-light text-stone-200 hover:bg-white/10 inline-flex items-center justify-center transition-colors",
  day_selected: "bg-champagne text-obsidian hover:bg-champagne hover:text-obsidian",
  day_today: "text-champagne underline underline-offset-4",
  day_outside: "text-stone-700",
  day_disabled: "text-stone-700 line-through opacity-40 hover:bg-transparent cursor-not-allowed",
};

export default function DatePicker({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const blocked = useBlockedDates();
  const selected = value ? parseISO(value) : undefined;
  const isBlocked = (d) => blocked.includes(format(d, "yyyy-MM-dd"));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="enquiry-date-input"
          disabled={disabled}
          className="w-full flex items-center justify-between bg-transparent border-b border-white/15 focus:border-champagne outline-none py-3 text-sm font-light text-left transition-colors duration-300"
        >
          <span className={selected ? "text-ivory" : "text-stone-600"}>
            {selected ? format(selected, "d MMMM yyyy") : "Choose your date"}
          </span>
          <CalendarDays size={14} className="text-stone-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-3 border-white/15 bg-[#14141A] rounded-none shadow-2xl" data-testid="enquiry-date-popover">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          disabled={[{ before: startOfToday() }, isBlocked]}
          modifiers={{ reserved: isBlocked }}
          modifiersClassNames={{ reserved: "reserved-day" }}
          classNames={DARK_CALENDAR}
          defaultMonth={selected}
        />
        <div className="mt-2 flex items-center gap-4 px-1 text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 bg-champagne" /> Selected</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 border border-stone-600 line-through" /> Reserved</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
